import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

import { selectUser } from 'store';
import { setToken } from 'store/user/userSlice';

import { useAppDispatch, useAppSelector } from 'hooks';
import { useScript } from 'hooks/useScript';
import { resolveConvexSiteUrl } from 'utils/convexUrls';
import { renewAccessTokenQuietly } from 'utils/services';

import {
  claimZoomChatIdentity,
  isZoomChatSdkLoaded,
  type ZoomChatIdentityClaim
} from './clearZoomChatIdentity';
import { useZoomChatRoutingHintsCleanup } from './zoomChatEscalation';
import {
  ZOOM_CHAT_CAMPAIGN_API_KEY,
  ZOOM_CHAT_CONFIG_GLOBAL,
  ZOOM_CHAT_ENV,
  ZOOM_CHAT_SCRIPT_SRC
} from './zoomChat.constants';
import {
  getZoomChatIdentityGeneration,
  subscribeToZoomChatIdentityGeneration
} from './zoomChatIdentityRevocation';
import {
  recordZoomChatCredentialIssued,
  recordZoomChatCredentialRequested,
  useZoomChatIdentityWatchdog
} from './zoomChatIdentityWatchdog';
import { createTraceId, reportIdentityOutcome } from './zoomChatTelemetry';
import { isHiddenZoomChatPath } from './useZoomChatVisibility';

type ZoomConsumerCredentials = {
  jwt: string;
  traceId?: string;
};

type ZoomChatHostConfig = {
  getAuthCredentials?: () => Promise<ZoomConsumerCredentials | null>;
};

type PatientSession = {
  accessToken?: string;
  refreshToken?: string;
  /**
   * The discriminator for "is this still the same patient". Deliberately not the access token:
   * that changes on every renewal, including our own, so comparing it would abandon the very
   * happy path renewal exists to serve. The patient id changes only when the patient does.
   */
  userId?: string;
};

/**
 * The identity claim, bound to the patient it was made for. The verdict alone is not a safe
 * gate: React state stays 'ready' for the PREVIOUS patient for one render when the user
 * changes, and the credential callback outlives renders entirely. Binding the patient id to
 * the verdict lets both consumers — the render gate and the issue-time gate — ask the only
 * question that matters: is the boundary ready for THIS patient (re-review 15).
 */
type ZoomChatIdentityClaimState = {
  patientId: string;
  verdict: ZoomChatIdentityClaim;
};

/**
 * The SDK injection, separated so it can be withheld. Rendered only after the identity claim
 * reports 'ready' — hooks cannot be called conditionally, but a component can be, and not
 * rendering this one is what "fail closed" physically is: no claim, no script tag, no chat.
 */
const ZoomChatSdkLoader = () => {
  // Mirrors Zoom's Embed Web Tag verbatim, including type="module" — the loader is an ES module
  // and throws if loaded as a classic script. The data attributes bootstrap and render the bubble;
  // once the runtime is up, `window.zoomCampaignSdk` has `hide()` and `show()` for it, which
  // useZoomChatBubbleVisibility drives from the slot.
  //
  // No `data-chat-entry-id`: Zoom documents that attribute as the alternative to a campaign, and
  // sending it binds the SDK to a bare entry point instead. An entry point carries no presentation
  // config, which is why the welcome screen was unreachable on that route. With the campaign tag,
  // Zoom picks the campaign by matching the page URL against its Campaign Location, so this stays
  // the same value in every environment.
  const [, scriptError] = useScript({
    checkForExisting: true,
    'data-apikey': ZOOM_CHAT_CAMPAIGN_API_KEY,
    'data-env': ZOOM_CHAT_ENV,
    src: ZOOM_CHAT_SCRIPT_SRC,
    type: 'module'
  });

  // Armed here rather than in the parent, so it watches only a document that actually has a script
  // tag in it: no injection, nothing to watch. It covers the two silent failures the transport
  // error below cannot see — see zoomChatIdentityWatchdog.ts.
  useZoomChatIdentityWatchdog();

  // Escalation routing hints are single-engagement: cleared the moment an engagement ends, so
  // the next chat the patient opens goes through the ordinary topic menu.
  useZoomChatRoutingHintsCleanup();

  /**
   * A loader failure used to be impossible to reach silently: a missing credential failed the
   * visibility gate, so the widget never mounted. Now the apikey is a constant and always present,
   * so a dark surface reaches nobody unless it is logged. Meanwhile the impression event still fires
   * on visibility, so telemetry reports the channel healthy regardless.
   *
   * Covers the transport only — Zoom's CDN unreachable, or the script blocked. A revoked apikey, a
   * host matching no campaign, and Zoom loading fine but never asking for a credential all leave
   * this silent, because the script still returns 200 and loads. Those are the watchdog's, above.
   */
  useEffect(() => {
    if (!scriptError) {
      return;
    }

    reportIdentityOutcome({
      level: 'warn',
      reason: 'sdk-script-load-failed',
      traceId: createTraceId()
    });
  }, [scriptError]);

  return null;
};

/**
 * Loads Zoom's chat client and supplies the patient's verified identity to it.
 *
 * Identity is not taken from this component's state. The token is minted server-side from
 * the patient's authenticated session, and Zoom verifies its signature against a public
 * key registered in Contact Center admin, so nothing the browser merely asserts is
 * trusted. Same shape as a Zoom video visit, where the backend signs a token and the SDK
 * trusts the token rather than the page.
 *
 * The config global is `zoomCampaignSdkConfig`, with the `Config` suffix. A previous
 * implementation wrote to `window.zoomCampaignSdk`, which does not exist, so combined
 * with optional chaining the identity call silently never ran.
 *
 * On any failure — no session, expired session, network error — this returns null rather
 * than a fallback identity. The agent then sees an unidentified visitor, which is correct;
 * showing them a stale or guessed identity would be worse than showing none.
 */
const ZoomChatWidget = () => {
  const { accessToken, refreshToken, userId } = useAppSelector(selectUser) ?? {};
  const dispatch = useAppDispatch();

  /**
   * The generation the credential hook is registered under, as the revocation module reports it.
   * Sign-out ends the generation in the same synchronous step as `clearUser`, but nothing this
   * component reads from the store has to change with it. A patient who verifies again as
   * themselves is cleared and set again in one task, so `userId` is the same before and after, and
   * App's loader, which shows only while a token is set and the id is empty, never remounts this
   * component. Subscribing is what tells it that its hook was taken out and has to be registered
   * again. Read through the subscription, not from the module during render, so the value the
   * effects below are keyed on is the one this render saw.
   */
  const identityGeneration = useSyncExternalStore(
    subscribeToZoomChatIdentityGeneration,
    getZoomChatIdentityGeneration
  );

  /**
   * The live session, read at call time rather than closed over.
   *
   * Zoom asks for a credential at moments we do not control, and the answer has to come from the
   * session that is current AT THAT MOMENT. Closing over the tokens instead made the hook depend
   * on them, so it was torn down and re-registered on every renewal, and — worse — an identity
   * request already in flight kept answering for a session that had since ended.
   */
  const sessionRef = useRef<PatientSession>({ accessToken, refreshToken, userId });

  // Also when the generation moves: the hook effect's cleanup blanks this ref, and a revocation that
  // leaves the same tokens in the store would otherwise leave the new hook with no session to read.
  useEffect(() => {
    sessionRef.current = { accessToken, refreshToken, userId };
  }, [accessToken, refreshToken, userId, identityGeneration]);

  /**
   * Whether Zoom's script may be injected yet. One-way: a document whose first page is a hidden one
   * (a link to a video visit, say) loads no Zoom script at all, so there is no bubble to flash and
   * nothing to hide, and the first page that is not hidden releases it for good. The identity hook
   * below does not wait for this, so a chat started on any later page still finds it.
   *
   * Released from the start when the script is already in the document: the app's loader remounts
   * this component, and on a hidden page that would otherwise take the watchdog off a client that
   * is still running. Nothing is injected then, and the bubble is the hook's to hide.
   */
  const pathname = useLocation()?.pathname ?? '/';
  const isPageHidden = isHiddenZoomChatPath(pathname);
  const [isSdkLoadReleased, setIsSdkLoadReleased] = useState(
    () => !isPageHidden || isZoomChatSdkLoaded()
  );

  useEffect(() => {
    if (!isPageHidden) {
      setIsSdkLoadReleased(true);
    }
  }, [isPageHidden]);

  /**
   * The identity boundary, and the gate the SDK loader hangs off. The claim must run — and report
   * clean — before the script tag is injected, so the SDK cannot pick up another patient's
   * leftovers; rendering the loader only on 'ready' makes that ordering structural rather than a
   * property of effect declaration order. Ownership is checked on the way IN because teardown
   * cannot cover the commonest session end: portal tokens live in sessionStorage, so closing the
   * tab ends the session without dispatching `user/clearUser`, and Zoom's localStorage keys
   * outlive it.
   *
   * 'cleanup-failed' is the fail-closed branch: the previous patient's state could not be fully
   * removed, so no chat rather than a chat that might resolve to them. Logged, because a patient
   * quietly losing the chat surface is otherwise invisible. 'eviction-pending' stays silent — a
   * reload is already in flight, and the fresh document runs its own claim.
   */
  const [identityClaim, setIdentityClaim] = useState<ZoomChatIdentityClaimState | null>(null);

  /**
   * The ref mirror of the claim, for the credential callback. The host hook is registered once
   * and reads everything at call time, so it needs the claim the same way it needs the session —
   * from a ref that is always current. Written BEFORE the state update, so no task that runs
   * between the effect and the re-render can observe a session newer than the claim.
   */
  const identityClaimRef = useRef<ZoomChatIdentityClaimState | null>(null);

  useEffect(() => {
    const claim: ZoomChatIdentityClaimState = {
      patientId: userId ?? '',
      verdict: claimZoomChatIdentity(userId ?? '')
    };

    identityClaimRef.current = claim;

    if (claim.verdict === 'cleanup-failed') {
      reportIdentityOutcome({
        level: 'warn',
        reason: 'identity-cleanup-failed',
        traceId: createTraceId()
      });
    }

    setIdentityClaim(claim);
  }, [userId]);

  // Registered before the script tag is injected below, so the hook exists whenever the SDK asks
  // for a credential. It asks more than once, and at times we do not control: at SDK start-up,
  // when an engagement begins, and on session restore. Every call has to succeed on its own,
  // which is why the 401 path below renews the session instead of giving up. A token renewal or a
  // page change does not register it again (`dispatch` is stable), so nothing re-registers it
  // mid-flight, and the slot keeps this component mounted across page changes while the chat is
  // allowed. App's loader and a session change can still unmount it, and a mount registers it anew.
  //
  // It is registered again when the patient changes (`userId`) and when the hook is revoked
  // (`identityGeneration`), because sign-out revokes the hook synchronously (see
  // zoomChatIdentityRevocation.ts) and a component that survives it would otherwise be left without
  // one for the rest of the document. Surviving it does not need a new patient: one who verifies
  // again as themselves is cleared and set again in one task, with the same id and no loader, so
  // the id alone cannot be what brings the hook back. A real sign-out does not leave one behind,
  // because the slot unmounts this component with the patient and the cleanup below takes it out.
  // The identity boundary in the hook still decides whether it answers.
  //
  // The claim above is not repeated for a revocation, on purpose. It answers who the patient is, and
  // that has not changed, so the verdict it holds is still the right one. What the sign-out sweep
  // owed Zoom's client stays owed (`evictionOwed` is module scope), and the next claim in this
  // document pays it, whoever it is for. Repeating the claim here would pay it at once, with a
  // reload in place of a page the patient has just verified on.
  useEffect(() => {
    const globals = window as unknown as Record<string, ZoomChatHostConfig | undefined>;

    // The generation this hook answers for. Sign-out ends it in the same synchronous step as the
    // `clearUser` dispatch, so the hook, and any reference Zoom kept to it, refuses from then on,
    // including during the gap before this component's cleanup runs. That gap is exactly when a
    // request already in flight finishes, and the session ref below is not yet blanked in it.
    // Taken from the render, not read here: a revocation between the render and this effect leaves
    // a hook that already refuses, where reading it now would register one for the new generation
    // with the session of the old one.
    const isRevoked = () => getZoomChatIdentityGeneration() !== identityGeneration;

    globals[ZOOM_CHAT_CONFIG_GLOBAL] = {
      ...globals[ZOOM_CHAT_CONFIG_GLOBAL],
      getAuthCredentials: async () => {
        // First statement in the hook, before every guard below. What the watchdog has to be able
        // to distinguish is Zoom asking and being refused — each refusal logs its own reason — from
        // Zoom never asking, which leaves no trace anywhere else. The record it returns is what a
        // later result is handed back to, so the watchdog knows which request that result answers.
        const credentialRequest = recordZoomChatCredentialRequested();

        const siteUrl = resolveConvexSiteUrl();

        // Generated up front so the same value is logged and returned. Zoom quotes this back
        // on a support ticket, and it only helps if it names a record that exists here.
        const traceId = createTraceId();

        // Before any work: a reference Zoom kept from before sign-out must not start a request for
        // a session that has ended, or for the one that replaced it.
        if (isRevoked()) {
          reportIdentityOutcome({ level: 'warn', reason: 'identity-revoked', traceId });
          return null;
        }

        const {
          accessToken: sessionAccessToken,
          refreshToken: sessionRefreshToken,
          userId: sessionUserId
        } = sessionRef.current;

        if (!siteUrl) {
          reportIdentityOutcome({ level: 'warn', reason: 'convex-url-missing', traceId });
          return null;
        }

        if (!sessionAccessToken) {
          reportIdentityOutcome({ level: 'warn', reason: 'no-patient-session', traceId });
          return null;
        }

        // The identity boundary, enforced at issue time — no token and no network unless the
        // claim is 'ready' AND names the patient whose session would sign this token. This is
        // the guard the render gate cannot provide: the hook outlives renders, and during
        // 'eviction-pending' the previous patient's ES module is still executing in this
        // document while the store already holds the replacement patient's session. A token
        // minted then hands the NEW patient's verified identity to the OLD patient's client
        // (re-review 15). The fresh document after the eviction reload makes its own claim and
        // issues normally.
        const identityBoundary = identityClaimRef.current;

        if (
          identityBoundary?.verdict !== 'ready' ||
          identityBoundary.patientId !== (sessionUserId ?? '')
        ) {
          reportIdentityOutcome({ level: 'warn', reason: 'identity-boundary-not-ready', traceId });
          return null;
        }

        const endpoint = new URL('/auth/zoom-consumer-token', siteUrl).toString();
        const requestToken = (bearer: string) =>
          fetch(endpoint, { headers: { Authorization: bearer }, method: 'GET' });

        try {
          let response = await requestToken(sessionAccessToken);

          // A sign-out that landed while this first request was in flight ends here, before the
          // renewal below can spend the ended session's refresh token.
          if (isRevoked()) {
            reportIdentityOutcome({
              level: 'warn',
              reason: 'session-changed-before-issue',
              traceId
            });
            return null;
          }

          // The portal's access token lives ten minutes, and this request deliberately does not
          // go through the RTK Query layer that renews it — that layer signs the patient out and
          // redirects on failure, which is far too destructive for a chat identity. So a tab left
          // idle reaches this point with an expired token. Renewing once keeps a verified patient
          // verified; without it the agent silently sees an unidentified visitor, which is the
          // failure this feature exists to prevent.
          if (response.status === 401 && sessionRefreshToken) {
            const renewed = await renewAccessTokenQuietly(sessionRefreshToken, sessionAccessToken);

            // Renewal is a network round trip, and the endpoint neither rotates nor revokes the
            // token presented to it, so it succeeds even for a session that ended while it was in
            // flight. Dispatching that result would write tokens onto a cleared store, which
            // re-persists them to the sessionStorage logout just wiped, satisfies the auth guard,
            // and signs the patient back in on a device they signed out of. Comparing against the
            // live session is what stops a vendor callback resurrecting one. The revocation is
            // asked first because it is the one that is already true while the ref is not.
            if (isRevoked() || sessionRef.current.refreshToken !== sessionRefreshToken) {
              reportIdentityOutcome({
                level: 'warn',
                reason: 'session-ended-during-renewal',
                traceId
              });
              return null;
            }

            if (renewed) {
              dispatch(setToken(renewed));
              response = await requestToken(renewed.accessToken);
            }
          }

          if (!response.ok) {
            reportIdentityOutcome({
              level: 'warn',
              reason: `token-endpoint-${response.status}`,
              traceId
            });
            return null;
          }

          const body = (await response.json()) as { loginUserIdJwt?: string };

          if (!body.loginUserIdJwt) {
            reportIdentityOutcome({
              level: 'warn',
              reason: 'token-missing-from-response',
              traceId
            });
            return null;
          }

          // Re-checked after the awaits, and checked for IDENTITY rather than presence. The
          // session can end between the request and the response, but it can also be REPLACED
          // without ever emptying — the sign-in-as-patient and deep-link flows swap one patient
          // for another in the same document. A presence-only check passes in that case and hands
          // Zoom the previous patient's verified identity while the next one is signed in, which
          // is the disclosure this feature exists to prevent. After every await above, so a
          // sign-out at any point in the request, renewal included, ends here with nothing returned.
          if (
            isRevoked() ||
            !sessionRef.current.accessToken ||
            sessionRef.current.userId !== sessionUserId
          ) {
            reportIdentityOutcome({
              level: 'warn',
              reason: 'session-changed-before-issue',
              traceId
            });
            return null;
          }

          // Recorded here and nowhere earlier: this is the only statement after which Zoom actually
          // holds an identity for this patient. Anything before it can still return null.
          recordZoomChatCredentialIssued(credentialRequest);

          reportIdentityOutcome({ level: 'info', reason: 'token-issued', traceId });

          return { jwt: body.loginUserIdJwt, traceId };
        } catch {
          reportIdentityOutcome({ level: 'warn', reason: 'token-request-failed', traceId });
          return null;
        }
      }
    };

    // Zoom's SDK stays loaded after this component unmounts, and a credential request may already
    // be in flight. Blanking the session matters as much as deleting the hook: the in-flight call
    // holds this ref, and an emptied ref is what makes it abandon its result rather than dispatch
    // tokens or answer Zoom for a patient who is no longer signed in here.
    return () => {
      sessionRef.current = {};

      const hostConfig = globals[ZOOM_CHAT_CONFIG_GLOBAL];

      if (hostConfig) {
        delete hostConfig.getAuthCredentials;
      }
    };
  }, [dispatch, userId, identityGeneration]);

  // Patient-bound, not verdict-only: on the render where the user has changed but the claim
  // effect has not yet run, the state still says 'ready' — for the PREVIOUS patient. Comparing
  // the claim's patient to the current one fails that render, so the loader can never be up for
  // a patient the boundary was not established for.
  if (identityClaim?.verdict !== 'ready' || identityClaim.patientId !== (userId ?? '')) {
    return null;
  }

  if (!isSdkLoadReleased) {
    return null;
  }

  return <ZoomChatSdkLoader />;
};

export default ZoomChatWidget;
